import React, { useState, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';

import useConfig from 'Context/ConfigContext/configHooks';
import { myProfile } from 'Redux/user-profile/selectors';
import { useCapacitor } from 'Util/Capacitors';
import usePopupAnnouncement from 'Routes/MyCourses/Domain/usePopupAnnouncement';
import { usePopupAnnouncementTracking } from 'Util/Analytics/usePopupAnnouncementTracking';
import useMyExternalProfile from 'Routes/Classroom/Domain/useMyExternalProfile';
import useCapacitorStorage from 'Util/Hooks/useCapacitorStorage';

import FeatureAnnouncementModal from '../../Components/FeatureAnnouncement';

import {
  getPrioritizedAnnouncement,
  calculateMarkAsReadStatus,
  getAnnouncementEventLabel,
} from './util';

const FeatureAnnouncementByUserType = ({ user }) => {
  const { openUrl } = useCapacitor();
  const { myCourses } = useConfig();
  const history = useHistory();
  const userId = user?.id ? `${user.id}_` : '';
  const enableFeatureAnnouncement = myCourses?.enableFeatureAnnouncement ?? false;
  const [readPopups, setReadPopups] = useCapacitorStorage([], `READ_POPUP_${userId}`);
  const {
    logOccurrence,
    logOnMoreThanTenSecond,
    logOnLastSlide,
    logOnCallToAction,
    logOnClose,
    logOnNotShowAgain,
  } = usePopupAnnouncementTracking();

  const { data: userExternalProfile } = useMyExternalProfile({ fetchPolicy: 'cache-only' });
  const userProviderName = userExternalProfile?.myExternalProfile?.providerName;

  const { popupAnnouncements, loading } = usePopupAnnouncement();
  const popupAnnouncement = useMemo(
    () => getPrioritizedAnnouncement(popupAnnouncements, readPopups),
    [loading]
  );

  const campaignName = popupAnnouncement?.title;
  const announcementEventLabel = getAnnouncementEventLabel(userProviderName, campaignName);

  const slideContent = popupAnnouncement?.detail?.contentV2?.slideContent ?? [];

  const [isSessionShowed, setIsSessionShowed] = useCapacitorStorage(
    false,
    `${userId}is_announcement_showed`
  );

  const [isShowModal, setIsShowModal] = useState(true);
  const isShowPopupAnnouncement =
    !loading && !isSessionShowed && isShowModal && slideContent.length > 0;

  const [markAsReadStatus, setMarkAsReadStatus] = useState({
    isStillOnPopupMoreThanTenSecond: false,
    isCheckDoNotShowAgain: false,
    isOnLastCarouselIndex: false,
    isClickCallToActionButton: false,
  });

  const isMeetMarkAsReadCondition = useMemo(
    () => calculateMarkAsReadStatus(markAsReadStatus, isShowPopupAnnouncement),
    [markAsReadStatus]
  );

  useEffect(() => {
    if (campaignName) {
      logOccurrence(announcementEventLabel, campaignName);
    }
  }, [popupAnnouncement]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setMarkAsReadStatus((prev) => ({
        ...prev,
        isStillOnPopupMoreThanTenSecond: true,
      }));
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const isStillOnPopupMoreThanTenSecond = markAsReadStatus.isStillOnPopupMoreThanTenSecond;
    if (isMeetMarkAsReadCondition) {
      if (popupAnnouncement?.id) setReadPopups((prev) => [...prev, popupAnnouncement.id]);
    }
    if (isMeetMarkAsReadCondition && isStillOnPopupMoreThanTenSecond) {
      logOnMoreThanTenSecond(announcementEventLabel, campaignName);
    }
  }, [isMeetMarkAsReadCondition]);

  const handleOpenUrl = (e, url) => {
    e.preventDefault();
    logOnCallToAction(announcementEventLabel, campaignName);
    setMarkAsReadStatus((prev) => ({ ...prev, isClickCallToActionButton: true }));
    if (url?.match('/mygoal')) history.push(url);
    if (url?.includes('http')) openUrl(url);
  };

  const handleComplete = (doNotShowAgain) => {
    setIsShowModal(false);
    setIsSessionShowed(true);
    logOnClose(announcementEventLabel, campaignName);
    if (doNotShowAgain) {
      logOnNotShowAgain(announcementEventLabel, campaignName);
      setMarkAsReadStatus((prev) => ({ ...prev, isCheckDoNotShowAgain: true }));
    }
  };

  const handleLastAnnouncementSlide = (isLastSlide) => {
    if (isLastSlide && slideContent.length > 1) {
      logOnLastSlide(announcementEventLabel, campaignName);
      setMarkAsReadStatus((prev) => ({ ...prev, isOnLastCarouselIndex: true }));
    }
  };

  return (
    <FeatureAnnouncementModal
      data-testid={'announcement-modal'}
      open={isShowPopupAnnouncement && enableFeatureAnnouncement}
      isCarouselAutoPlay={false}
      announcements={slideContent}
      announcementId={popupAnnouncement?.id}
      onCompleted={handleComplete}
      handleLastAnnouncementSlide={handleLastAnnouncementSlide}
      onBannerClick={handleOpenUrl}
      onDetailClick={handleOpenUrl}
    />
  );
};

export default connect((state) => ({ user: myProfile(state) }), {})(FeatureAnnouncementByUserType);
